"use client";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Filter, Eye } from "lucide-react";
import { Button } from "@stockline/ui/components/button";
import { Checkbox } from "@stockline/ui/components/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { ScrollArea } from "@stockline/ui/components/scroll-area";

/**
 * Column show/hide menu for the ExcelViewer. Lists every column with a
 * checkbox (checked = visible); built for spreadsheets with many columns, so
 * the list is scrollable and offers bulk "Show all" / "Hide all".
 */
export function ExcelColumnsMenu({
  columnCount,
  getLabel,
  hiddenColumns,
  onToggle,
  onShowAll,
  onHideAll,
}: {
  readonly columnCount: number;
  readonly getLabel: (colIndex: number) => string;
  readonly hiddenColumns: ReadonlySet<number>;
  readonly onToggle: (colIndex: number) => void;
  readonly onShowAll: () => void;
  readonly onHideAll: () => void;
}) {
  const { t } = useTranslation();
  const indices = useMemo(
    () => Array.from({ length: columnCount }, (_, i) => i),
    [columnCount],
  );
  const hiddenCount = hiddenColumns.size;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={hiddenCount > 0 ? "default" : "ghost"}
          size="sm"
          className="h-7 gap-1 px-2"
          title={t("documents.viewer.filterColumns")}
        >
          <Filter className="h-4 w-4" />
          {hiddenCount > 0 && (
            <span className="text-xs tabular-nums">{hiddenCount}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-xs font-medium text-gray-600">
            {t("documents.viewer.shownCount", {
              shown: columnCount - hiddenCount,
              total: columnCount,
            })}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={onShowAll}
              disabled={hiddenCount === 0}
            >
              {t("documents.viewer.showAll")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={onHideAll}
              disabled={hiddenCount >= columnCount}
            >
              {t("documents.viewer.hideAll")}
            </Button>
          </div>
        </div>
        <ScrollArea className="h-72">
          <ul className="p-1">
            {indices.map((colIndex) => {
              const visible = !hiddenColumns.has(colIndex);
              return (
                <li key={colIndex}>
                  <button
                    type="button"
                    onClick={() => onToggle(colIndex)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-100"
                  >
                    <Checkbox
                      checked={visible}
                      className="pointer-events-none"
                      tabIndex={-1}
                    />
                    <span className="truncate text-gray-700">
                      {getLabel(colIndex)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
        {hiddenCount > 0 && (
          <div className="flex items-center gap-1.5 border-t px-3 py-1.5 text-[11px] text-gray-500">
            <Eye className="h-3 w-3" />
            {t("documents.viewer.hideColumnHint")}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
